<template>
<base-section id="registration">
    <v-container>
    <base-section-heading
        title="Registration Form"
        color="secondary"
        align="left"
        :toColor="true"
    />
    If you encounter any issue during the process of the registration, please send an email to: <strong>lats2025@gmail.com</strong>
    <br>We remind that the papers to be presented at the Symposium need at least one author registered with <strong>FULL registration</strong>, in order to include the paper in the LATS2025 proceedings.
    <br><br>
    <div class="form-container" ref="formContainer">
    <v-card-text class="pb-2">
      <span class="caption-custom font-italic"><strong>* = Required fields</strong></span>
    </v-card-text>
    <v-stepper v-model="currentStep">
        <!-- Stepper Headers -->
        <v-stepper-header>
        <v-stepper-step :complete="currentStep > 1" step="1">Personal Information</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="currentStep > 2" step="2">Paper Information</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="currentStep > 3" step="3">Practical Information</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="currentStep > 4" step="4">Summary</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step step="5">Payment</v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
        <!-- Step 1: Personal Information -->
        <v-stepper-content step="1">
            <v-form ref="step1Form" v-model="step1Valid">
            <v-text-field
                v-model="formData.name"
                label="Name *"
                :rules="nameRules"
                required
            ></v-text-field>
            <v-text-field
                v-model="formData.surname"
                label="Surname *"
                :rules="nameRules"
                required
            ></v-text-field>
            <v-text-field
                v-model="formData.affiliation"
                label="Affiliation *"
                :rules="nameRules"
                required
            ></v-text-field>
            <v-text-field
                v-model="formData.email"
                label="Email *"
                :rules="emailRules"
                type="email"
                required
            ></v-text-field>
            <br><br>
            <v-btn color="primary" @click="nextStep" :disabled="!step1Valid">Continue</v-btn>
            </v-form>
        </v-stepper-content>

        <!-- Step 2: Paper Information -->
        <v-stepper-content step="2">
            If you want to register for an accepted paper to present at the conference, please provide the Paper ID below, otherwise leave it empty.
            <br> Please mind that there must be at least one full registration for each accepted paper.
            <v-form ref="step2Form" v-model="step2Valid">
            <v-text-field
                v-model="formData.paperId"
                label="Paper ID (optional)"
            ></v-text-field>
            <br>
            Are you an IEEE member?
            <v-switch
                v-model="formData.isIEEEMember"
                :label="`${formData.isIEEEMember ? 'Yes' : 'No'}`"
            ></v-switch>
            <v-text-field
                v-if="formData.isIEEEMember"
                v-model="formData.ieeeNumber"
                label="IEEE Member Number *"
                :rules="requiredRules"
                required
            ></v-text-field>

            <br>
            Please select from the Menu below your registration type. To visualize the IEEE member registration types you should answer "Yes" to the IEEE member question above.
            <v-select
                v-model="formData.subscriptionType"
                :items="subscriptionItems"
                label="Select Registration Type *"
                :rules="requiredRules"
                required
            ></v-select>

            <v-card
                v-if="formData.subscriptionType"
                class="my-4 pa-4"
                outlined
            >
            <div class="text-h6">Amount to be paid: {{ calculateAmount }} USD</div>
            </v-card>
            <br><br>
            <v-btn text @click="currentStep = 1">Back</v-btn>
            <v-btn color="primary" @click="nextStep" :disabled="!step2Valid">Continue</v-btn>
            </v-form>
        </v-stepper-content>

        <!-- Step 3: Practical Information -->
        <v-stepper-content step="3">
            <v-form ref="step3Form" v-model="step3Valid">
                Where do you plan to stay during the conference?
            <v-text-field
                v-model="formData.hotelLocation"
                label="Hotel Location *"
                :rules="requiredRules"
                required
            ></v-text-field>
            What is your estimated date of arrival and departure?
            <v-menu
            v-model="arrivalDateMenu"
            :close-on-content-click="false"
            max-width="290"
            >
            <template v-slot:activator="{ on, attrs }">
            <v-text-field
                v-model="formData.arrivalDate"
                label="Arrival Date *"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="arrivalDateRules"
                required
            ></v-text-field>
            </template>
            <v-date-picker
            v-model="formData.arrivalDate"
            @input="arrivalDateMenu = false"
            :show-current="false"
            :value="defaultDate"
            ></v-date-picker>
        </v-menu>

            <v-menu
                v-model="departureDateMenu"
                :close-on-content-click="false"
                max-width="290"
            >
            <template v-slot:activator="{ on, attrs }">
            <v-text-field
                v-model="formData.departureDate"
                label="Departure Date *"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="departureDateRules"
                required
            ></v-text-field>
            </template>
            <v-date-picker
            v-model="formData.departureDate"
            @input="departureDateMenu = false"
            :min="formData.arrivalDate || ''"
            :show-current="false"
            :value="defaultDate"
            ></v-date-picker>
            </v-menu>

            <!--
            <v-textarea
                v-model="formData.dietaryRestrictions"
                label="Dietary Restrictions (if any)"
                rows="3"
            ></v-textarea>
            -->
            <br>
            Please specify your <strong>dietary restrictions</strong> *:
            <v-radio-group
                v-model="formData.dietaryRestrictions"
                :rules="requiredRules"
                required
            >
            <v-radio
                v-for="option in dietaryOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
            ></v-radio>
            </v-radio-group>

            <v-text-field
                v-if="formData.dietaryRestrictions === 'other'"
                v-model="formData.dietaryRestrictionsOther"
                label="Please specify your dietary restrictions *"
                :rules="otherDietaryRules"
                required
            ></v-text-field>

            Do you have any allergies we should be aware of?
            <v-text-field
                v-model="formData.allergies"
                label="Allergies (optional)"
            ></v-text-field>

            <br><br>
            <v-btn text @click="currentStep = 2">Back</v-btn>
            <v-btn color="primary" @click="nextStep" :disabled="!step3Valid">Continue</v-btn>
            </v-form>
        </v-stepper-content>

        <!-- Step 4: Summary -->
        <v-stepper-content step="4">
            <v-card class="mb-4" outlined>
            <v-card-text>
                <h3>Personal Information</h3>
                <p>Name: {{ formData.name }}</p>
                <p>Surname: {{ formData.surname }}</p>
                <p>Affiliation: {{ formData.affiliation }}</p>
                <p>Email: {{ formData.email }}</p>
                <br>
                <h3 class="mt-4">Paper and Registration Type Information</h3>
                <p>Paper ID: {{ formData.paperId || 'Not provided' }}</p>
                <p>IEEE Member: {{ formData.isIEEEMember ? 'Yes' : 'No' }}</p>
                <p v-if="formData.isIEEEMember">IEEE Number: {{ formData.ieeeNumber }}</p>
                <p>Registration Type: {{ displaySubscription }}</p>

                <br>
                <h3 class="mt-4">Practical Information</h3>
                <p>Hotel Location: {{ formData.hotelLocation }}</p>
                <p>Arrival Date: {{ formData.arrivalDate }}</p>
                <p>Departure Date: {{ formData.departureDate }}</p>
                <p>Dietary Restrictions: {{ displayDietaryRestrictions }}</p>
                <p>Allergies: {{ displayAllergies }}</p>
            </v-card-text>
            </v-card>
            <br><br>
            <v-btn text @click="currentStep = 3">Back</v-btn>
            <v-btn color="primary" @click="nextStep" class="nowrap">Confirm and Proceed to Payment</v-btn>
        </v-stepper-content>

        <!-- Step 5: Payment -->
        <v-stepper-content step="5">
          <v-row align="center" class="mb-4">
            <v-col cols="auto">
              <span>The payment process is supplied by</span>
            </v-col>
            <v-col cols="auto">
              <a
                href="https://www.acofi.edu.co/"
                target="_blank"
              >
              <v-img
                src="@/assets/acofi.png"
                max-height="80"
                max-width="100"
                contain
              ></v-img>
              </a>
            </v-col>
          </v-row>
            <h3>Select Payment Method</h3>
            <v-radio-group
                v-model="formData.paymentMethod"
                :rules="requiredRules"
                required
            >
                <v-radio label="Credit Card" value="credit_card"></v-radio>
                <v-radio label="Bank Transfer" value="bank_transfer"></v-radio>
            </v-radio-group>

            <!-- Payment Information Messages -->
            <v-alert
                v-if="formData.paymentMethod === 'bank_transfer'"
                type="info"
                class="mt-4"
            >
                In order to pay with a bank transfer, you will be provided with bank information to send the payment after having clicked on "Complete Registration" button below.
            </v-alert>

            <v-alert
                v-if="formData.paymentMethod === 'credit_card'"
                type="info"
                class="mt-4"
            >
                In order to complete the payment with credit card, you will be redirected to another website in charge of managing the payment. Click on "Complete Registration" to continue.
            </v-alert>
        <v-btn text @click="currentStep = 4">Back</v-btn>
        <v-btn
            color="success"
            @click="showConfirmDialog"
            :disabled="!formData.paymentMethod"
            class="nowrap"
        >
            Complete Registration
        </v-btn>
        </v-stepper-content>

        <!-- Registration Confirmation Dialog -->
        <v-dialog v-model="showConfirmationDialog" max-width="400">
            <v-card>
            <v-card-title class="headline">
                Confirm Registration
            </v-card-title>
            <v-card-text>
                Are you sure do you want to complete the registration and proceed to payment?
                <vue-recaptcha
                  ref="recaptcha"
                  :sitekey="siteKey"
                  @verify="onVerify"
                  @expired="onExpired"
                  class="mt-4"
                />
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                color="grey darken-1"
                text
                @click="showConfirmationDialog = false"
                :disabled="isSubmitting"
                >
                No
                </v-btn>
                <v-btn
                color="primary"
                @click="confirmRegistration"
                :loading="isSubmitting"
                :disabled="isSubmitting || !recaptchaVerified"
                >
                Yes
                </v-btn>
            </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Error Dialog -->
        <v-dialog v-model="errorDialog" max-width="400">
          <v-card>
            <v-card-title class="error white--text">Error</v-card-title>
            <v-card-text class="pt-4">
              {{ submitError }}
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                text
                @click="errorDialog = false"
              >
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Success View (Add this after the v-stepper component) -->
        <v-dialog
        v-model="showSuccessDialog"
        persistent
        max-width="600"
        >
        <v-card>
            <v-card-title class="headline">
            Complete payment
            </v-card-title>
            <v-card-text>
            <!-- Bank Transfer Information -->
            <div v-if="formData.paymentMethod === 'bank_transfer'">
                {{ submitMessage }}
                <v-alert
                type="warning"
                class="mt-4"
                >
                <p>Please be aware that you must complete your payment of {{ calculateAmount }} USD to succesfully complete your registration process.</p>
            </v-alert>
                <v-card outlined class="pa-4">
                <p><strong>Bank Name:</strong> BANCOLOMBIA</p>
                <p><strong>Account Holder:</strong> Asociación Colombiana de Facultades de Ingeniería ACOFI</p>
                <p><strong>Account Number:</strong> 17126041844</p>
                <p><strong>SWIFT/BIC:</strong> COLOCOBM</p>
                <p><strong>Amount:</strong> {{ calculateAmount }} USD</p>
                </v-card>
                <br>
                If you have issues in the registration process, please contact <strong>lats2025@gmail.com</strong>.
            </div>

            <!-- Credit Card Information -->
            <div v-if="formData.paymentMethod === 'credit_card'">
                {{ submitMessage }}
                <v-alert
                type="warning"
                class="mt-4"
                >
                <p>Please be aware that you must complete your payment of {{ calculateAmount }} USD to succesfully complete your registration process.</p>
            </v-alert>
            <h3>To complete the payment you must:</h3>
                 - Click on the button below
            <br> - Fill all the fields with your personal information, please use the <strong>same email address</strong> you inserted here
            <br> - Select <strong>LATS 2025</strong> from the dropdown menu "Concepto"
            <br> - Select the currency you would like to use to pay
            <br> - Insert the amount to pay, that in your case is <strong>{{ calculateAmount }} USD</strong>, or <strong> {{ calculateAmountCOP }} COP</strong>
            <br> - Complete the payment by credit card<br>
                <v-btn
                color="primary"
                block
                class="nowrap mt-4"
                href="https://micrositios.goupagos.com.co/acofi-ma"
                target="_blank"
                dark
                >
                <v-icon
                    left
                    dark
                >
                    mdi-open-in-new
                </v-icon>
                Proceed to Payment
                </v-btn>
                <br>
                If you have issues in the registration process, please contact <strong>lats2025@gmail.com</strong>.
            </div>
            </v-card-text>
            <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
                color="primary"
                text
                @click="attemptToClose"
            >
                Close
            </v-btn>
            </v-card-actions>
        </v-card>
        </v-dialog>

        <!-- Close Confirmation Dialog -->
        <v-dialog v-model="showCloseConfirmationDialog" max-width="400">
            <v-card>
            <v-card-title>Confirm Close</v-card-title>
            <v-card-text>
                Are you sure you want to close? Please be sure to save the previous information.
            </v-card-text>
            <v-card-actions class="justify-center">
                <v-btn text @click="showCloseConfirmationDialog = false">No</v-btn>
                <v-btn color="primary" @click="confirmClose" class="nowrap">
                  <p class="text-wrap" style="width: min-content; margin: auto;">Yes, I saved the information
                  </p>
                </v-btn>
            </v-card-actions>
            </v-card>
        </v-dialog>
        </v-stepper-items>
    </v-stepper>
    </div>
    </v-container>
</base-section>
</template>
<script src="https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit" async defer></script>
<script>
import { attempt } from 'lodash';
import DataService from '../../services/DataService';
import { VueRecaptcha } from 'vue-recaptcha';

var md5 = require("blueimp-md5")

export default {
name: 'Registration',
components: { VueRecaptcha },
data: () => ({
    currentStep: 1,
    step1Valid: false,
    step2Valid: false,
    step3Valid: false,
    arrivalDateMenu: false,
    departureDateMenu: false,
    showSuccessDialog: false,
    showConfirmationDialog: false,
    showCloseConfirmationDialog: false,
    errorDialog: false,
    submitError: null,
    isSubmitting: false,
    siteKey: '6LenM8gqAAAAAMpmGPKj2uyee-o2dlst7mRKNIf3',
    recaptchaVerified: false,
    recaptchaToken: null,
    submitMessage: 'Your registration has been recorded.',
    dietaryOptions: [
      { label: 'None', value: 'none' },
      { label: 'Vegan', value: 'vegan' },
      { label: 'Vegetarian', value: 'vegetarian' },
      { label: 'Pescatarian', value: 'pescatarian' },
      { label: 'No Pork', value: 'no_pork' },
      { label: 'Other', value: 'other' },
    ],
    subscriptionOptions: [
      { label: 'Professional IEEE Member (FULL)', value: 'ieee_regular' },
      { label: 'Student IEEE Member', value: 'ieee_student' },
      { label: 'IEEE Fellow Member', value: 'ieee_fellow' },
      { label: 'IEEE Life Member', value: 'ieee_life'},
      { label: 'Invited speaker', value: 'invited' },
      { label: 'Professional NON-Member (FULL)', value: 'regular' },
      { label: 'Student NON-Member', value: 'student' },
    ],
    subscriptionPrices: {
      ieee_regular: 600,
      ieee_student: 330,
      ieee_fellow: 500,
      ieee_life: 330,
      invited: 500,
      regular: 700,
      student: 400,
    },
    formData: {
      name: '',
      surname: '',
      affiliation: '',
      email: '',
      paperId: '',
      isIEEEMember: false,
      ieeeNumber: '',
      hotelLocation: '',
      arrivalDate: '',
      departureDate: '',
      dietaryRestrictions: '',
      subscriptionType: '',
      allergies: '',
      paymentMethod: '',
      USD: '',
      COP: '',
      subscriptionString: '',
      dietaryString: '',
    },
    defaultDate: '2025-03-01',
    nameRules: [
    v => !!v || 'Field is required',
    v => v.length >= 2 || 'Minimum 2 characters',
    v => {
      const invalidChars = v.match(/[^\p{L}{0-9} .-]/gu) || [];
      return invalidChars.length === 0 || `Invalid characters found: ${[...new Set(invalidChars)].join(', ')}`;
    },
    // v => /^[\p{L} .-]+$/u.test(v) || 'Invalid characters',
    ],
    emailRules: [
    v => !!v || 'E-mail is required',
    v => /.+@.+\..+/.test(v) || 'E-mail must be valid',
    ],
    requiredRules: [
    v => !!v || 'Field is required',
    ],
    otherDietaryRules: [
      v => !!v || 'Please specify your dietary restrictions',
    ],
    arrivalDateRules: [
      v => !!v || 'Arrival date is required',
    ],
}),

watch: {
    currentStep() {
      this.$nextTick(() => {
        const container = this.$refs.formContainer;
        if (container) {
          container.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'center',
          });
        }
      });
    },
    'formData.arrivalDate'(newDate) {
      // If departure date is before arrival date, clear it
      if (this.formData.departureDate && new Date(this.formData.departureDate) <= new Date(newDate)) {
        this.formData.departureDate = '';
      }
    },
    'formData.isIEEEMember'(newValue) {
      // Clear the selected subscription type when IEEE member status changes
      this.formData.subscriptionType = ''
    },
    'formData.dietaryRestrictions'(newValue) {
      if (newValue !== 'other') {
        this.formData.dietaryRestrictionsOther = '';
      }
    },
  },

computed: {

    departureDateRules() {
      return [
        (v) => !!v || 'Departure date is required',
        (v) => {
          if (!v || !this.formData.arrivalDate) return true;
          return new Date(v) > new Date(this.formData.arrivalDate) || 'Departure date must be after arrival date'
        },
      ]
    },
    displayAllergies() {
      if (this.formData.allergies != '') {
        return this.formData.allergies;
      }
      else {
        return 'None';
      }
    },
    displayDietaryRestrictions() {
      if (this.formData.dietaryRestrictions === 'other') {
        this.formData.dietaryString = this.formData.dietaryRestrictionsOther;
        return this.formData.dietaryRestrictionsOther;
      }
      const option = this.dietaryOptions.find(opt => opt.value === this.formData.dietaryRestrictions);
      this.formData.dietaryString = option ? option.label : ''
      return option ? option.label : '';
    },
    displaySubscription() {
      const option = this.subscriptionOptions.find(opt => opt.value === this.formData.subscriptionType);
      this.formData.subscriptionString = option ? option.label : '';
      return option ? option.label : '';
    },
    calculateAmount() {
      const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
      const formattedPrice = formatter.format(this.subscriptionPrices[this.formData.subscriptionType] || 0);
      this.formData.USD = this.subscriptionPrices[this.formData.subscriptionType] || 0
      return formattedPrice
    },
    calculateAmountCOP() {
      const formatter = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' });
      const formattedPrice = formatter.format(this.subscriptionPrices[this.formData.subscriptionType] * 4350 || 0);
      this.formData.COP = this.subscriptionPrices[this.formData.subscriptionType] * 4350 || 0;
      return formattedPrice;
    },
    subscriptionItems() {
        if (this.formData.isIEEEMember) {
        return [
            { text: 'Professional IEEE Member (FULL) ($600)', value: 'ieee_regular' },
            { text: 'Invited Speaker ($500)', value: 'invited' },
            { text: 'IEEE Fellow Member ($500)', value: 'ieee_fellow' },
            { text: 'IEEE Life Member ($330)', value: 'ieee_life' },
            { text: 'Student IEEE Member ($330)', value: 'ieee_student' },
        ]
        } else {
        return [
            { text: 'Professional NON-Member (FULL) ($700)', value: 'regular' },
            { text: 'Invited Speaker ($500)', value: 'invited' },
            { text: 'Student NON-Member ($400)', value: 'student' },
        ]
        }
    },
},

methods: {

    computeAllergies() {
      if (this.formData.allergies === '') {
        this.formData.allergies = 'None';
      }
    },

    calculateAmountM() {
      this.formData.USD = this.subscriptionPrices[this.formData.subscriptionType] || 0
    },

    calculateAmountCOPM() {
      this.formData.COP = this.subscriptionPrices[this.formData.subscriptionType] * 4350 || 0;
    },

    onVerify(token) {
      this.recaptchaVerified = true
      this.recaptchaToken = token
    },

    onExpired() {
      this.recaptchaVerified = false
      this.recaptchaToken = null
    },

    nextStep() {
    if (this.currentStep < 5) {
        this.currentStep += 1;
    }
    },

    async confirmRegistration() {
      if (!this.recaptchaVerified) return
      this.isSubmitting = true;
      this.submitError = null;
      this.calculateAmountM();
      this.calculateAmountCOPM();
      this.computeAllergies();

      // console.log(this.formData);

      const registrationData = {
      ...this.formData,
      recaptchaToken: this.recaptchaToken
      };

      try {
        const response = await DataService.submitRegistration(registrationData);
        this.showConfirmationDialog = false;
        this.showSuccessDialog = true;
        this.submitMessage = response.data.message || 'Your registration has been recorded.'
      } catch (error) {
        this.showConfirmationDialog = false;
        this.submitError = error.response?.data?.message || 'An error occurred during registration. Please try again later or send a message to lats2025@gmail.com';
        this.errorDialog = true;
      } finally {
        // Reset recaptcha
        if (this.$refs.recaptcha) {
          this.$refs.recaptcha.reset();
        }
        this.recaptchaVerified = false;
        this.recaptchaToken = null;
        this.isSubmitting = false;
      }
    },

    showConfirmDialog() {
      this.showConfirmationDialog = true;
    },

    attemptToClose() {
      this.showCloseConfirmationDialog = true;
    },

    confirmClose() {
      this.showCloseConfirmationDialog = false;
      this.showSuccessDialog = false;
      // Optionally redirect to home page or another page
      this.$router.push('/');
      // Refresh the page
    },
},
}
</script>

<style>
.v-application .grey--text.text--darken-1 {
color: black !important;
}
.v-application .grey--text.text--lighten-1 {
color: white !important;
}

.v-btn {
  min-height: 36px !important;
  height: auto !important;
}

.nowrap .v-btn__content {
  width: 100%;
  white-space: break-spaces;
  text-align: center;
}

.caption-custom {
  font-size: 0.87rem !important;
  color: rgba(0, 0, 0, 0.6);
}

.form-container {
  scroll-margin-top: 90px;
}

.vue-recaptcha {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}
</style>